import PropTypes from "prop-types"
import React from "react"

const ServiceLocation = ({ locations, selectedLocation, practice, theme }) => {
  // Early return if practice is not provided
  if (!practice) {
    return null
  }
  const formatAddress = (location) => {
    if (!location) return ""

    if (location.kind === "virtual") return "Virtual Session"
    if (location.kind === "home") return "At Your Home"

    // For office/physical locations, show the actual address if available
    if (location.kind === "office" || location.kind === "physical") {
      if (location.address) {
        return location.address
      }
      if (location.city && location.stateAbbreviation) {
        return `${location.city}, ${location.stateAbbreviation}`
      }
      if (location.city && location.state) {
        return `${location.city}, ${location.state}`
      }
      return "In Office"
    }

    return location.address || `${location.city}, ${location.stateAbbreviation}`
  }

  const getDisplayLocation = () => {
    if (selectedLocation) {
      return selectedLocation
    } else if (locations && locations.length > 0) {
      // Default to first physical location
      return locations.find((loc) => loc.kind === "physical") || locations[0]
    }
    return null
  }

  const displayLocation = getDisplayLocation()

  // Get practice location data for map
  const getPracticeLocationData = () => {
    // First try the selected location (this is the service's location)
    if (displayLocation && displayLocation.latitude && displayLocation.longitude) {
      return {
        lat: displayLocation.latitude,
        lng: displayLocation.longitude,
        address: formatAddress(displayLocation)
      }
    }

    // Then try practice office locations (prioritize office over personal)
    if (practice && practice.locations && practice.locations.length > 0) {
      // First try to find office location
      const officeLocation = practice.locations.find((loc) => loc.kind === "office")
      if (officeLocation && officeLocation.latitude && officeLocation.longitude) {
        return {
          lat: officeLocation.latitude,
          lng: officeLocation.longitude,
          address: formatAddress(officeLocation)
        }
      }

      // Then try physical location as fallback
      const physicalLocation = practice.locations.find((loc) => loc.kind === "physical")
      if (physicalLocation && physicalLocation.latitude && physicalLocation.longitude) {
        return {
          lat: physicalLocation.latitude,
          lng: physicalLocation.longitude,
          address: formatAddress(physicalLocation)
        }
      }
    }

    // Finally try practice.location (personal location) as last resort
    if (practice && practice.location && practice.location.latitude && practice.location.longitude) {
      return {
        lat: practice.location.latitude,
        lng: practice.location.longitude,
        address:
          practice.location.city && practice.location.state
            ? `${practice.location.city}, ${practice.location.state}`
            : practice.location.address || ""
      }
    }

    return null
  }

  const locationData = getPracticeLocationData()

  // Don't show location section for virtual-only services or if hide_address is true
  if (!displayLocation || displayLocation.kind === "virtual" || displayLocation.hide_address) {
    return null
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 overflow-hidden">
      <h3
        className="w-full text-[27px] font-bold leading-[40px]"
        style={{
          fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
          color: theme?.colors?.textDark || "#55433f"
        }}>
        Location
      </h3>

      <div className="flex w-full items-center justify-start gap-4">
        <div className="flex flex-1 flex-col items-start justify-center gap-6">
          {/* Map - only show if GPS coordinates are available */}
          {locationData && locationData.lat && locationData.lng && (
            <div className="relative h-[200px] w-full overflow-hidden rounded-xl bg-gray-200">
              {/* Google Maps iframe */}
              <iframe
                className="h-full w-full"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                  locationData.address
                )}`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Map pin overlay */}
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div
                  className="h-6 w-6 rounded-full border-2 border-white shadow-lg"
                  style={{ backgroundColor: theme?.colors?.primary || "#bd654f" }}></div>
              </div>
            </div>
          )}

          {/* Address */}
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start justify-start gap-1">
              <div
                className="text-[18px] font-normal leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#55433f"
                }}>
                {displayLocation.kind === "home" ? "Service provided at your home" : formatAddress(displayLocation)}
              </div>
              {/* Show additional location details if available */}
              {displayLocation.kind !== "home" && displayLocation.kind !== "virtual" && (
                <div className="flex flex-col items-start justify-start gap-1">
                  {displayLocation.name && (
                    <div
                      className="text-[16px] font-medium leading-[24px]"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                        color: theme?.colors?.textDark || "#55433f"
                      }}>
                      {displayLocation.name}
                    </div>
                  )}
                  {displayLocation.phone && (
                    <div
                      className="text-[14px] font-normal leading-[20px]"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                        color: theme?.colors?.textGrey || "#666666"
                      }}>
                      {displayLocation.phone}
                    </div>
                  )}
                </div>
              )}
            </div>

            {displayLocation.kind !== "home" && locationData && locationData.lat && locationData.lng && (
              <button
                onClick={() => {
                  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    locationData.address
                  )}`
                  window.open(mapUrl, "_blank")
                }}
                className="font-semibold underline decoration-solid transition-colors hover:no-underline"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.primary || "#bd654f",
                  borderRadius: theme?.buttonShape?.borderRadius || "0.375rem"
                }}>
                View map
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

ServiceLocation.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      city: PropTypes.string,
      stateAbbreviation: PropTypes.string,
      state: PropTypes.string,
      address: PropTypes.string,
      name: PropTypes.string,
      phone: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]).isRequired,
      hide_address: PropTypes.bool
    })
  ),
  selectedLocation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    city: PropTypes.string,
    stateAbbreviation: PropTypes.string,
    state: PropTypes.string,
    address: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]).isRequired,
    hide_address: PropTypes.bool
  }),
  practice: PropTypes.shape({
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
        address: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number
      })
    ]),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.oneOf(["physical", "virtual", "home", "office"]),
        city: PropTypes.string,
        stateAbbreviation: PropTypes.string,
        address: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number
      })
    )
  }),
  theme: PropTypes.object
}

export default ServiceLocation
